import { Component, inject, Injector, Input, OnInit } from '@angular/core';
import { mapToObject } from '@sama/js-core';
import { matches } from '@sama/micro-dash';
import { skip } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { AbstractWorkspaceComponent } from '../../shared/abstract-workspace-component';
import { Input as TaskInput } from '../../team-apis/otterdam/input';
import { Project } from '../../team-apis/otterdam/project';
import { Task } from '../../team-apis/otterdam/task';
import { initializeAnnotations } from '../http/loading/shape-initializer-utils';
import { TimerService } from '../production-workspace/timer-badge/timer.service';
import { workspaceProviders } from '../workspace-providers';

const hardcodedFakeProductionProjectId = 14877;

// eslint-disable-next-line @angular-eslint/prefer-on-push-component-change-detection -- Outputs don't show up correctly with OnPush :(. Not sure why.
@Component({
  selector: 'h3-preview-workspace',
  templateUrl: './preview-workspace.component.html',
  styleUrls: ['./preview-workspace.component.scss'],
  providers: workspaceProviders,
  standalone: false,
})
export class PreviewWorkspaceComponent
  extends AbstractWorkspaceComponent
  implements OnInit
{
  @Input({ required: true }) project!: Project;
  @Input({ required: true }) stepId!: number;
  @Input({ required: true }) layout!: string;
  @Input({ required: true }) jsCode!: string;

  questionStore = this.store('task')('data');

  private timerService = inject(TimerService);

  constructor(injector: Injector) {
    super('preview', injector);
  }

  override async ngOnInit() {
    await super.ngOnInit();
    this.subscribeTo(this.questionStore.$.pipe(skip(1)), () => {
      this.store.setTask(this.store.state().task);
    });
    super.updateFromStepInfo({ layout: this.layout, js_code: this.jsCode });
  }

  protected buildWorkspaceConfig() {
    if (
      environment.name === 'production' &&
      this.project.outputs.find(
        (output) =>
          output.type === 'PointCloudOutput' ||
          output.type === 'SensorFusionOutput',
      )
    ) {
      this.project.id = hardcodedFakeProductionProjectId;
    }
    return {
      name: this.name,
      projectId: this.project.id,
      stepId: this.stepId,
      user: this.currentUser,
    };
  }

  protected override async loadModels() {
    this.modelsLoading$.next('loading');
    this.store.setProject(this.project);
    initializeAnnotations(this.store, this.timerService, []);
    await Promise.all([
      this.authorizationService.load(),
      this.featureFlagLoadingService.load(),
      this.taxonomyService.load(),
    ]);
    this.store.setTask(this.generatePreviewTask());
  }

  protected override handleModelsLoaded() {
    this.modelsLoading$.next('loaded');
    this.setUpEvents();
  }

  private setUpEvents() {
    this.eventTrackingService.addContext({
      task_id: undefined,
      round: undefined,
    });
  }

  private generatePreviewTask(): Task {
    return {
      id: this.getFakeTaskId(),
      next_internal_id: 1,
      answers_or_gold: {},
      round: 0,
      data: mapToObject(this.project.inputs, (input) => [
        input.name,
        this.generatePreviewQuestion(input),
      ]),
      default_answers: {},
      assets_archived_at: null,
      _extras: {},
    };
  }

  private getFakeTaskId() {
    switch (this.project.id) {
      case 9:
        return environment.seedDataPointCloudTaskId;
      case 10:
        return environment.seedDataSensorFusionTaskId;
      case hardcodedFakeProductionProjectId:
        return environment.seedDataPointCloudTaskId;
      default:
        return 'preview_task';
    }
  }

  // eslint-disable-next-line complexity -- if you need to modify this code, fix this first
  private generatePreviewQuestion(input: TaskInput) {
    switch (input.format) {
      case 'Integer':
        return 42;
      case 'String':
        return `[${input.name}]`;
      case 'Date':
        return '2008-11-01';
      case 'URL':
        return this.generatePreviewUrl(input);
      case 'Computed':
        return this.generateComputedInput(input);
    }
  }

  private generatePreviewUrl(input: TaskInput) {
    const output = this.project.outputs.find(
      matches({ meta: { input_id: input.id } }),
    );

    switch (output && output.type) {
      case 'VideoOutput':
        return 'https://s3.amazonaws.com/sama-client-assets/development/videos/seed-zip-files/traffic-2.zip';
      case 'PointCloudOutput':
        return environment.previewWorkspacePointCloudAssetUrl;
      default:
        return 'https://s3.amazonaws.com/sama-client-assets/development/SeedImages/image_tagging/dog1.jpg';
    }
  }

  private generateComputedInput(input: TaskInput) {
    switch (input.sub_type) {
      case 'min':
        return '[-80,-40,-30]';
      case 'max':
        return '[80,50,10]';
      default:
        return undefined;
    }
  }
}
